import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { Bar, BarChart, Cell, XAxis, YAxis } from "recharts"

import type { DashboardUseCase } from "../../types/use-case.types"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

import { usdFormatter } from "./usd-tooltip-formatter"
import { formatUSD } from "../../utils/format-helpers"

type BenefitBarChartProps = {
  useCases: DashboardUseCase[]
}

export function BenefitBarChart({ useCases }: BenefitBarChartProps) {
  const { t } = useTranslation()

  const chartConfig = useMemo(
    () =>
      ({
        value: {
          label: t("useCases.dashboard.charts.annualBenefit"),
          color: "hsl(var(--chart-1))",
        },
      }) satisfies ChartConfig,
    [t]
  )

  const data = useMemo(() => {
    return [...useCases]
      .sort((a, b) => b.annualBenefit - a.annualBenefit)
      .slice(0, 15)
      .map((uc) => ({
        name: uc.ucName.length > 25 ? uc.ucName.slice(0, 22) + "..." : uc.ucName,
        fullName: uc.ucName,
        value: uc.annualBenefit,
      }))
  }, [useCases])

  if (data.length === 0) return null

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("useCases.dashboard.charts.benefitByUseCase")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer
          className="w-full"
          config={chartConfig}
          style={{ height: Math.max(200, data.length * 32) }}
        >
          <BarChart
            data={data}
            layout="vertical"
            margin={{ left: 10, right: 30, top: 5, bottom: 5 }}
          >
            <XAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatUSD(v)} type="number" />
            <YAxis dataKey="name" tick={{ fontSize: 11 }} type="category" width={140} />
            <ChartTooltip
              content={<ChartTooltipContent />}
              formatter={usdFormatter}
              labelFormatter={(_label, payload) => payload[0]?.payload?.fullName ?? _label}
            />
            <Bar
              dataKey="value"
              radius={[0, 4, 4, 0]}
              stroke="hsl(var(--foreground))"
              strokeWidth={1}
            >
              {data.map((_, i) => (
                <Cell key={i} fill="hsl(var(--chart-1))" />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
